/** @module components/cloze-quiz */


import Model from '@lernetz/ts-lib/src/core/model/Model';
import {View, ViewRegistrationMap} from '@lernetz/ts-lib/src/core/view/View';
import Repeat from '@lernetz/ts-lib/src/components/Repeat';

import {Paragraph} from './Paragraph';
import {SelectSpan, SelectSpanView} from './SelectSpan';
import {SelectSpanChoice} from './SelectSpanChoice';
import {SelectSpanPlaceholderChoice} from './SelectSpanPlaceholderChoice';
import {Span} from './Span';
import {TextSpan, TextSpanView} from './TextSpan';

import {Quiz, QuizInputState, QuizVisitor} from '../quiz';

import {ClozeQuiz as template} from '../../templates/cloze-quiz';


/**
 * [[QuizVisitor]]s that support [[ClozeQuiz]] should implement this interface.
 */
export interface ClozeQuizVisitor<TResult = void> extends QuizVisitor<TResult> {
    visitClozeQuiz(quiz: ClozeQuiz): TResult;
}


function isClozeQuizVisitor<TResult>(visitor: QuizVisitor<TResult>): visitor is ClozeQuizVisitor<TResult> {
    return typeof visitor['visitClozeQuiz'] !== 'undefined';
}


/**
 * Specifies the initial state of a [[ClozeQuiz]].
 */
export interface ClozeQuizInitialState {

    /**
     * Sets the paragraphs in the cloze quiz.
     */
    paragraphs: Paragraph[];
}


/**
 * A type of [[Quiz]] that models a cloze quiz.
 *
 * @example
 *```typescript
 *
 * const clozeQuiz = new ClozeQuiz({
 *     paragraphs: [
 *         {
 *             spans: [
 *                 new TextSpan({ text: 'This is a ' }),
 *                 new SelectSpan({
 *                     choices: [
 *                         new SelectSpanPlaceholderChoice({ text: '' }),
 *                         new SelectSpanChoice({ text: 'cloze', correct: true }),
 *                         new SelectSpanChoice({ text: 'drag & drop', correct: false }),
 *                         new SelectSpanChoice({ text: 'hot-spot', correct: false }),
 *                     ]
 *                 }),
 *                 new TextSpan({ text: ' quiz.' }),
 *             ]
 *         },
 *     ]
 * });
 *
 * const alternateClozeQuiz = createClozeQuizFrom('This is a {cloze*;drag & drop;hot-spot} quiz.\n\nAnother paragraph.');
 *```
 *
 * @seealso [[Quiz]], the main interface implemented by this class.
 */
export class ClozeQuiz extends Model implements Quiz {

    /**
     * Initializes a new instance of the [[ClozeQuiz]] class.
     *
     * @param initialState Specifies the initial state for the new instance.
     * @param data An optional object to be passed through to the base class' ([[Model]]) constructor.
     */
    public constructor(initialState: ClozeQuizInitialState, data: object = {}) {
        super({
            inputState: 'none',
            paragraphs: [],
            ...initialState,
            ...data
        });

        const choices: SelectSpanChoice[] = [];
        for (const paragraph of this.paragraphs) {
            for (const span of paragraph.spans) {
                if (span instanceof SelectSpan) {
                    choices.push(...span.choices);
                }
            }
        }
        this.choices = choices;

        for (const choice of choices) {
            choice.change.filter(({ name }) => name === 'selected')
                         .subscribe(this.updateInputState.bind(this));
        }

        this.updateInputState();
    }

    /**
     * Gets the paragraphs in this quiz.
     */
    public readonly paragraphs: Paragraph[];

    private readonly choices: SelectSpanChoice[];

    /** @inheritdoc */
    public readonly inputState: QuizInputState;

    /**
     * Dispatches to [[ClozeQuizVisitor.visitClozeQuiz]] (if implemented by `visitor`).
     */
    public accept<TResult = void>(visitor: QuizVisitor<TResult>): TResult {
        if (isClozeQuizVisitor(visitor)) {
            return visitor.visitClozeQuiz(this);
        }
    }

    /**
     * Updates the [[inputState]] property.
     */
    protected updateInputState(): void {
        let inputState: QuizInputState;

        if (this.choices.every(ch => ch.selected === ch instanceof SelectSpanPlaceholderChoice)) {
            inputState = 'none';
        } else if (this.choices.some(ch => ch.selected && !ch.correct && !(ch instanceof SelectSpanPlaceholderChoice))) {
            inputState = 'incorrect';
        } else if (this.choices.every(ch => ch.selected === ch.correct)) {
            inputState = 'correct';
        }
        else {
            inputState = 'incomplete';
        }

        this.set('inputState', inputState);
    }
}


export class ClozeQuizView extends View<ClozeQuiz> {

    public constructor() {
        super({
            template,
            registrations: ClozeQuizView.getRegistrations(),
         });
    }

    private static getRegistrations(): ViewRegistrationMap {
        const registrations = new ViewRegistrationMap();
        registrations.set('repeat', Repeat);
        registrations.set(TextSpan, TextSpanView);
        registrations.set(SelectSpan, SelectSpanView);
        return registrations;
    }
}


/**
 * This helper function creates a [[ClozeQuiz]] instance from the given formatted text.
 * This is an alternative to building a structured [[ClozeQuizInitialState]] object manually.
 *
 * @param text The formatted plain text (no HTML!). Choices must be formatted as `{a;b;c*}`.
 * @param data An optional arbitrary object that will be forwarded to [[ClozeQuiz]]' constructor.
 *
 * @example
 ```typescript
 const clozeQuiz = createClozeQuizFromText('This is a {cloze*;drag & drop;hot-spot} quiz.');
 ```
 */
export function createClozeQuizFromText(text: string, data: object = {}): ClozeQuiz {

    const texts = text.replace('\r\n', '\n').split('\n\n');
    const paragraphs = texts.map(createParagraph);
    return new ClozeQuiz({ paragraphs }, data);

    function createParagraph(text: string): Paragraph {
        const spans: Span[] = [];

        let index = 0;
        while (index < text.length) {
            const openingBraceIndex = text.indexOf('{', index);
            if (openingBraceIndex < 0) {
                spans.push(new TextSpan({ text: text.substr(index) }));
                index = text.length;
            }
            else {
                const closingBraceIndex = text.indexOf('}', openingBraceIndex);
                if (closingBraceIndex < openingBraceIndex) {
                    throw new Error('Invalid cloze quiz text format: } missing.');
                }
                if (openingBraceIndex > index) {
                    spans.push(new TextSpan({ text: text.substr(index, openingBraceIndex - index) }));
                }
                const choiceTexts = text.substr(openingBraceIndex + 1, closingBraceIndex - openingBraceIndex - 1).split(';');
                const choices: SelectSpanChoice[] = [
                    new SelectSpanPlaceholderChoice({ text: '' }),
                ];
                for (const choiceText of choiceTexts) {
                    if (!choiceText.length) continue;
                    const correct = choiceText.endsWith('*');
                    const text = correct ? choiceText.substr(0, choiceText.length - 1) : choiceText;
                    choices.push(new SelectSpanChoice({ text, correct }));
                }
                spans.push(new SelectSpan({ choices }));
                index = closingBraceIndex + 1;
            }
        }

        return { spans };
    }
}


/**
 * A set of view registrations for [[ChoiceQuiz]].
 */
export const registrations = new ViewRegistrationMap();
registrations.set(ClozeQuiz, ClozeQuizView);
